import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
    login,
    signInWithGoogle,
    setAuthPersistence,
} from '../../firebase/auth.service';
import { handleGoogleSignIn } from '../../firebase/user.service';
import { auth } from '../../firebase/firebase.config';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [keepSignedIn, setKeepSignedIn] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [showGoogleConfirmModal, setShowGoogleConfirmModal] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const [redirect, setRedirect] = useState(false);

    // Show success message passed from password reset flow
    const resetSuccess = location.state?.resetSuccess || '';

    useEffect(() => {
        let timer;
        if (success) {
            timer = setTimeout(() => setRedirect(true), 1200);
        }
        return () => clearTimeout(timer);
    }, [success]);

    useEffect(() => {
        if (redirect) {
            navigate('/dashboard');
        }
    }, [redirect, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!email || !password) {
            setError('Please fill in all fields');
            return;
        }

        setLoading(true);
        try {
            const normalizedEmail = email.trim().toLowerCase();

            console.info('[login]', {
                event: 'login_started',
                email: normalizedEmail,
                keepSignedIn,
                timestamp: new Date().toISOString(),
            });

            // Set Firebase persistence BEFORE signing in
            await setAuthPersistence(keepSignedIn);
            await login(normalizedEmail, password);
            const currentUser = auth.currentUser;

            console.info('[login]', {
                event: 'auth_success',
                uid: currentUser.uid,
                email: normalizedEmail,
                timestamp: new Date().toISOString(),
            });

            localStorage.setItem('uid', currentUser.uid);

            console.info('[login]', {
                event: 'login_success',
                uid: currentUser.uid,
                timestamp: new Date().toISOString(),
            });

            // Route guard will check profile + onboardingCompleted
            // and redirect to /complete-profile if needed
            setSuccess('Login successful! Redirecting...');
        } catch (err) {
            console.error('Login error:', err);
            const errorMsg = err.code === 'auth/user-not-found'
                ? 'Email not found. Please sign up first.'
                : err.code === 'auth/wrong-password'
                    ? 'Incorrect password.'
                    : err.message || 'Login failed';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleButtonClick = () => {
        setError('');
        setSuccess('');
        setShowGoogleConfirmModal(true);
    };

    const handleCancelGoogleConfirm = () => {
        setShowGoogleConfirmModal(false);
    };

    const handleConfirmGoogleLogin = async () => {
        setError('');
        setSuccess('');
        setGoogleLoading(true);

        try {
            console.info('[auth.singleProvider]', {
                flow: 'login',
                event: 'google_login_confirmed',
                timestamp: new Date().toISOString(),
            });

            const result = await signInWithGoogle();
            const firebaseUser = result.user;

            console.info('[auth.singleProvider]', {
                flow: 'login',
                event: 'google_auth_success',
                uid: firebaseUser.uid,
                email: firebaseUser.email || null,
                timestamp: new Date().toISOString(),
            });

            const { needsOnboarding } = await handleGoogleSignIn(firebaseUser);

            if (needsOnboarding) {
                navigate('/complete-profile');
                return;
            }

            localStorage.setItem('uid', firebaseUser.uid);
            setSuccess('Login successful! Redirecting...');
        } catch (err) {
            console.error('Google login error:', err);
            const code = err.code || '';
            if (code === 'auth/popup-closed-by-user') {
                // User closed popup
            } else if (code === 'auth/cancelled-popup-request') {
                // Duplicate popup
            } else {
                setError(err.message || 'Google sign-in failed. Please try again.');
            }
        } finally {
            setGoogleLoading(false);
            setShowGoogleConfirmModal(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-2 py-8">
            <form className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 md:p-10 flex flex-col gap-2" onSubmit={handleSubmit}>
                <h2 className="text-2xl md:text-3xl font-bold mb-1">Login</h2>
                <p className="text-gray-500 text-base mb-4">You get secure access to patients and payments the moment you log in</p>
                <label className="text-sm font-medium">Email</label>
                <div className="flex items-center bg-gray-100 rounded-md px-3 mt-1">
                    <span className="text-gray-400 mr-2">📧</span>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        required
                        className="w-full bg-transparent outline-none py-3 text-base"
                        disabled={loading || googleLoading}
                    />
                </div>
                <label className="text-sm font-medium mt-4">Password</label>
                <div className="flex items-center bg-gray-100 rounded-md px-3 mt-1">
                    <span className="text-gray-400 mr-2">🔒</span>
                    <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        required
                        className="w-full bg-transparent outline-none py-3 text-base"
                        autoComplete="current-password"
                        disabled={loading || googleLoading}
                    />
                    <span
                        className="ml-2 text-gray-400 cursor-pointer select-none"
                        onClick={() => setShowPassword((prev) => !prev)}
                        title={showPassword ? 'Hide password' : 'Show password'}
                    >
                        {showPassword ? '🙈' : '👁️'}
                    </span>
                </div>
                <div className="flex items-center justify-between mt-3 mb-2 flex-wrap gap-2">
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={keepSignedIn}
                            onChange={() => setKeepSignedIn((prev) => !prev)}
                            className="accent-black"
                            disabled={loading || googleLoading}
                        />
                        Keep me signed in on this device
                    </label>
                    <Link to="/forgot-password" className="text-gray-500 text-sm underline hover:text-black">Forgot password?</Link>
                </div>
                {resetSuccess && <div className="text-green-600 text-sm text-center mt-2">{resetSuccess}</div>}
                {error && <div className="text-red-500 text-sm text-center mt-2">{error}</div>}
                {success && <div className="text-green-600 text-sm text-center mt-2">{success}</div>}
                <button
                    className="w-full bg-black text-white rounded-md py-3 font-semibold text-lg mt-2 hover:bg-gray-900 transition-colors disabled:opacity-60"
                    type="submit"
                    disabled={loading || googleLoading}
                >
                    {loading ? 'Signing in...' : 'Sign in'}
                </button>

                <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-sm text-gray-400">or</span>
                    <div className="flex-1 h-px bg-gray-200" />
                </div>

                <button
                    type="button"
                    onClick={handleGoogleButtonClick}
                    disabled={loading || googleLoading}
                    className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-md py-3 font-medium text-base hover:bg-gray-50 transition-colors disabled:opacity-60"
                >
                    {googleLoading ? (
                        <svg
                            className="animate-spin h-5 w-5 text-gray-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                    ) : (
                        <svg width="20" height="20" viewBox="0 0 48 48">
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                        </svg>
                    )}
                    {googleLoading ? 'Signing in...' : 'Continue with Google'}
                </button>

                <div className="text-center text-sm text-gray-600 mt-4">
                    Don't have an account? <Link to="/signup" className="text-black font-semibold hover:underline">Sign up</Link>
                </div>
                <div className="mt-5 text-xs text-gray-500 text-center">
                    <span>
                        You agree to the <a href="#" className="underline">Terms</a>, <a href="#" className="underline">Privacy Policy</a>, <a href="#" className="underline">Refund & Cancellation</a>, and <a href="#" className="underline">Shipping & Delivery</a> when you sign in
                    </span>
                </div>
            </form>

            {showGoogleConfirmModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                        <h3 className="text-xl font-bold">Continue with Google?</h3>
                        <p className="mt-3 text-sm text-gray-600">
                            If an account already exists with a different sign-in method, continuing will replace the previous provider with Google.
                            You may not be able to use the previous login method after this.
                        </p>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleCancelGoogleConfirm}
                                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50"
                                disabled={googleLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmGoogleLogin}
                                className="rounded-md bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900 disabled:opacity-60"
                                disabled={googleLoading}
                            >
                                Continue with Google
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Login;
